import { normalizeWeeklyTimeWindows } from "weekly-time-scheduler";

// Re-exported, never redeclared: the editor mapping is the identity function,
// and that only stays true if both ends name the same type.
export type { DayOfWeek, WeeklyTimeWindow as ScheduleWindow } from "weekly-time-scheduler";
import type { DayOfWeek, WeeklyTimeWindow as ScheduleWindow } from "weekly-time-scheduler";

export type ScheduleConditionValue = {
  timezone: string;
  windows: ScheduleWindow[];
};

const MINUTES_PER_DAY = 1440;
const SLOT_MINUTES = 15;

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export function isValidIanaTimeZone(tz: unknown): tz is string {
  if (typeof tz !== "string" || tz.length === 0) return false;
  // Modern Intl also accepts fixed offsets ("+01:00"), which cannot follow DST
  // and so must not back a weekly schedule. Every IANA name starts with a letter.
  if (!/^[A-Za-z]/.test(tz)) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function getBrowserTimeZone(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidIanaTimeZone(tz) ? tz : "UTC";
}

// `Intl.supportedValuesOf` is ES2022; this project's lib is ES2020, so the
// method is described here rather than widening the compiler target.
type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: "timeZone") => string[];
};

export function getSupportedTimeZones(): string[] {
  const supported = (Intl as IntlWithSupportedValues).supportedValuesOf?.("timeZone");
  if (supported && supported.length > 0) return [...supported];

  // Older engines have no enumeration API; offer what we can still name.
  const browser = getBrowserTimeZone();
  return browser === "UTC" ? ["UTC"] : [browser, "UTC"];
}

/** Build a window without repeating the `as DayOfWeek` cast at every call site. */
function win(dayOfWeek: number, startMinute: number, endMinute: number): ScheduleWindow {
  return { dayOfWeek: dayOfWeek as DayOfWeek, startMinute, endMinute };
}

const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKEND = [6, 7];
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

export const PRESETS: Record<string, ScheduleWindow[]> = {
  business: WEEKDAYS.map((d) => win(d, 9 * 60, 17 * 60)),
  offHours: [
    ...WEEKDAYS.flatMap((d) => [win(d, 0, 9 * 60), win(d, 17 * 60, MINUTES_PER_DAY)]),
    ...WEEKEND.map((d) => win(d, 0, MINUTES_PER_DAY)),
  ],
  weekends: WEEKEND.map((d) => win(d, 0, MINUTES_PER_DAY)),
  allWeek: ALL_DAYS.map((d) => win(d, 0, MINUTES_PER_DAY)),
};

export const PRESET_LABELS: { key: keyof typeof PRESETS; label: string }[] = [
  { key: "business", label: "Business hours" },
  { key: "offHours", label: "Outside business hours" },
  { key: "weekends", label: "Weekends" },
  { key: "allWeek", label: "All week" },
];

export function getDefaultScheduleValue(): ScheduleConditionValue {
  return { timezone: getBrowserTimeZone(), windows: PRESETS.business };
}

/**
 * Everything that reads a persisted condition goes through this.
 * `RuleCondition.value` is `any` off JSONB and WeeklyTimeScheduler is a
 * controlled component, so it must never be handed unvetted data.
 */
export function coerceScheduleValue(raw: unknown): ScheduleConditionValue {
  const source = (raw ?? {}) as { timezone?: unknown; windows?: unknown };
  const timezone = isValidIanaTimeZone(source.timezone) ? source.timezone : getBrowserTimeZone();
  return { timezone, windows: normalizeWeeklyTimeWindows(source.windows) };
}

function isSlotAligned(minute: number): boolean {
  return minute % SLOT_MINUTES === 0;
}

/** Returns a list of human-readable problems; empty means valid. */
export function validateScheduleValue(value: unknown): string[] {
  const problems: string[] = [];
  const v = (value ?? {}) as { timezone?: unknown; windows?: unknown };

  if (!isValidIanaTimeZone(v.timezone)) {
    problems.push("a valid IANA timezone is required");
  }

  if (!Array.isArray(v.windows)) {
    problems.push("the schedule is missing its time windows");
    return problems;
  }

  if (v.windows.length === 0) {
    problems.push("at least one time window is required");
    return problems;
  }

  v.windows.forEach((rawWindow, index) => {
    const w = (rawWindow ?? {}) as Partial<ScheduleWindow>;
    const label = `window ${index + 1}`;

    if (!Number.isInteger(w.dayOfWeek) || (w.dayOfWeek as number) < 1 || (w.dayOfWeek as number) > 7) {
      problems.push(`${label} has an invalid day`);
      return;
    }

    const { startMinute, endMinute } = w;
    const minutesValid = [startMinute, endMinute].every(
      (m) => Number.isInteger(m) && (m as number) >= 0 && (m as number) <= MINUTES_PER_DAY
    );
    if (!minutesValid) {
      problems.push(`${label} has times outside 00:00–24:00`);
      return;
    }

    if ((startMinute as number) >= (endMinute as number)) {
      problems.push(`${label} ends before it starts`);
      return;
    }

    // Not pedantry: the package silently drops off-grid windows, so without
    // this a rule could save and come back with fewer windows than it went in with.
    if (!isSlotAligned(startMinute as number) || !isSlotAligned(endMinute as number)) {
      problems.push(`${label} is not aligned to 15 minutes`);
    }
  });

  return problems;
}

function formatMinute(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function formatWindowTimes(windows: ScheduleWindow[]): string {
  return windows
    .slice()
    .sort((a, b) => a.startMinute - b.startMinute)
    .map((w) => `${formatMinute(w.startMinute)}–${formatMinute(w.endMinute)}`)
    .join(", ");
}

/**
 * Window text only — the "Active" label belongs to the summary component, so
 * this stays reusable and testable.
 */
export function formatScheduleSummary(value: ScheduleConditionValue): string {
  const { windows, timezone } = value;
  if (!windows || windows.length === 0) return "";

  const byDay = new Map<number, ScheduleWindow[]>();
  for (const w of windows) {
    const bucket = byDay.get(w.dayOfWeek);
    if (bucket) bucket.push(w);
    else byDay.set(w.dayOfWeek, [w]);
  }

  // Group runs of *consecutive* days that share an identical set of time spans.
  const groups: { days: number[]; times: string }[] = [];
  for (const day of ALL_DAYS) {
    const dayWindows = byDay.get(day);
    if (!dayWindows) continue;

    const times = formatWindowTimes(dayWindows);
    const previous = groups[groups.length - 1];
    const isConsecutive = previous && previous.days[previous.days.length - 1] === day - 1;

    if (isConsecutive && previous.times === times) previous.days.push(day);
    else groups.push({ days: [day], times });
  }

  const rendered = groups.map(({ days, times }) => {
    const first = DAY_LABELS[days[0] - 1];
    const last = DAY_LABELS[days[days.length - 1] - 1];
    const dayText = days.length === 1 ? first : `${first}–${last}`;
    return `${dayText} ${times}`;
  });

  const shown = rendered.slice(0, 3).join(", ");
  const overflow = rendered.length > 3 ? ` +${rendered.length - 3} more` : "";

  return `${shown}${overflow} (${timezone})`;
}
